import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';

/** Filas (mcdu) de atendimento do Instant herdadas do legado. */
export enum InstantChatMcdu {
  Service = 7788,
  Complaint = 7799
}

export type InstantChatStatus = 'idle' | 'loading' | 'ready' | 'error';
export type InstantChatOpenResult = 'opened' | 'loading' | 'unavailable';

export const INSTANT_CHAT_SCRIPT_ID = 'instant-chat';
/** Launcher que o Instant injeta; o legado abria o chat com `document.getElementById('InstantChat').click()`. */
export const INSTANT_CHAT_LAUNCHER_ID = 'InstantChat';
/** Classe no `<html>`: a janela do Instant fica no `body`, fora do host `.dark-mode` do layout. */
export const INSTANT_CHAT_DARK_CLASS = 'instant-chat-dark';
/** Classe no `<html>` que reexibe o launcher do Instant quando o portal não encontra como abrir o chat. */
export const INSTANT_CHAT_NATIVE_LAUNCHER_CLASS = 'instant-chat-native-launcher';
/** Classe no `<html>` com a janela do chat expandida para ocupar a tela do portal. */
export const INSTANT_CHAT_EXPANDED_CLASS = 'instant-chat-expanded';
export const INSTANT_CHAT_EXPAND_BUTTON_ID = 'instant-chat-expand';
export const INSTANT_CHAT_LOAD_TIMEOUT_MS = 20000;

// DOM que o Instant (Live Helper Chat v1) cria no `body` ao abrir o chat; `lhc-delayed` o esconde ao fechar.
const VENDOR_CONTAINER_ID = 'lhc_container';
const VENDOR_HEADER_ID = 'lhc_header';
const VENDOR_MINIMIZE_ID = 'lhc_min';
const VENDOR_HIDDEN_CLASS = 'lhc-delayed';

const INSTANT_CHAT_BASE_URL =
  'https://mic.instant.com.br/chat/index.php/por/chat/getstatus/(click)/internal/(position)/bottom_right/(top)/390/(units)/pixels/(leaveamessage)/false/(department)/2/(mcdu)/';

/** O script do Instant lê `LHCChatOptions.opt` sem checar se existe (o legado definia em `index.html`). */
const INSTANT_CHAT_OPTIONS = { widget_height: 540, widget_width: 380, popup_height: 520, popup_width: 500 };

interface InstantChatWindow extends Window {
  LHCChatOptions?: { opt?: Record<string, number> };
  // Live Helper Chat v1 (base do getstatus do Instant): `var lh_inst` global; o launcher usa `onclick="return lh_inst.lh_openchatWindow()"`.
  lh_inst?: { lh_openchatWindow?: () => unknown };
}

/** Só origem e caminho: query string e fragment podem carregar dados temporários/sensíveis. Inválida vira vazio. */
function toSafePageUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  try {
    const url = new URL(raw);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
    return `${url.origin}${url.pathname}`;
  } catch {
    return '';
  }
}

/**
 * Chat externo Instant da área autenticada.
 *
 * PENDENTE (bloqueia produção): o script de terceiro roda no documento do portal, cujo token fica em
 * `localStorage`. Não há alternativa isolada (iframe/sandbox) nem API de encerramento documentadas pelo
 * fornecedor; o widget injetado permanece até recarregar a página. `stop()` só impede o portal de abri-lo.
 * O script só carrega se o CSP do ingress liberar `https://mic.instant.com.br` em `script-src`.
 */
@Injectable({ providedIn: 'root' })
export class InstantChatService {
  private readonly document = inject(DOCUMENT);
  private readonly statusSignal = signal<InstantChatStatus>('idle');
  readonly status = this.statusSignal.asReadonly();
  private readonly expandedSignal = signal(false);
  readonly expanded = this.expandedSignal.asReadonly();
  private active = false;
  private mcdu: number = InstantChatMcdu.Service;
  private loadTimeout?: ReturnType<typeof setTimeout>;
  private vendorObserver?: MutationObserver;
  private readonly onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.expandedSignal()) this.setExpanded(false);
  };

  start(mcdu: number = InstantChatMcdu.Service): void {
    this.active = true;
    this.mcdu = mcdu;
    const status = this.statusSignal();
    if (status === 'error') {
      this.retry();
    } else if (status === 'idle') {
      this.injectScript();
    } else if (status === 'ready') {
      this.syncNativeLauncher();
    }
    this.watchVendorDom();
  }

  /** Recarrega o script após falha; não faz nada enquanto carrega ou depois de pronto. */
  retry(): void {
    if (!this.active || this.statusSignal() === 'loading' || this.statusSignal() === 'ready') return;
    this.injectScript();
  }

  open(): InstantChatOpenResult {
    if (!this.active) return 'unavailable';
    const opener = this.findOpener();
    if (opener) {
      opener();
      return 'opened';
    }
    return this.statusSignal() === 'loading' ? 'loading' : 'unavailable';
  }

  stop(): void {
    this.active = false;
    this.setExpanded(false);
    this.vendorObserver?.disconnect();
    this.vendorObserver = undefined;
    this.document.removeEventListener('keydown', this.onKeydown);
    this.document.getElementById(INSTANT_CHAT_EXPAND_BUTTON_ID)?.remove();
    this.setDarkMode(false);
    this.document.documentElement.classList.remove(INSTANT_CHAT_NATIVE_LAUNCHER_CLASS);
  }

  toggleExpanded(): void {
    if (!this.active) return;
    this.setExpanded(!this.expandedSignal());
  }

  setDarkMode(dark: boolean): void {
    this.document.documentElement.classList.toggle(INSTANT_CHAT_DARK_CLASS, dark);
  }

  buildUrl(mcdu: number): string {
    const queue = Number.isInteger(mcdu) && mcdu > 0 ? mcdu : InstantChatMcdu.Service;
    const referrer = encodeURIComponent(toSafePageUrl(this.document.referrer));
    const location = encodeURIComponent(toSafePageUrl(this.document.location?.href));
    return `${INSTANT_CHAT_BASE_URL}${queue}/?r=${referrer}&l=${location}`;
  }

  private injectScript(): void {
    this.clearLoadTimeout();
    this.document.getElementById(INSTANT_CHAT_SCRIPT_ID)?.remove();

    const script = this.document.createElement('script');
    script.id = INSTANT_CHAT_SCRIPT_ID;
    script.type = 'text/javascript';
    script.async = true;
    script.src = this.buildUrl(this.mcdu);
    script.addEventListener('load', () => this.onScriptLoad(script));
    script.addEventListener('error', () => this.onScriptError(script));

    const win = this.document.defaultView as InstantChatWindow | null;
    if (win && !win.LHCChatOptions) {
      win.LHCChatOptions = { opt: { ...INSTANT_CHAT_OPTIONS } };
    }

    this.statusSignal.set('loading');
    this.syncNativeLauncher();
    const firstScript = this.document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      this.document.head.appendChild(script);
    }
    this.loadTimeout = setTimeout(() => this.onScriptError(script), INSTANT_CHAT_LOAD_TIMEOUT_MS);
  }

  // Um `load` também ocorre quando o Instant devolve script vazio (ex.: atendimento offline): exige o mecanismo de abertura.
  private onScriptLoad(script: HTMLScriptElement): void {
    if (!this.isCurrent(script)) return;
    this.clearLoadTimeout();
    this.statusSignal.set(this.findOpener() ? 'ready' : 'error');
    this.syncNativeLauncher();
  }

  // Bloqueio por CSP, falha de rede e timeout chegam aqui; um `load` tardio após o timeout ainda recupera o estado.
  private onScriptError(script: HTMLScriptElement): void {
    if (!this.isCurrent(script) || this.statusSignal() !== 'loading') return;
    this.clearLoadTimeout();
    this.statusSignal.set('error');
    this.syncNativeLauncher();
  }

  private isCurrent(script: HTMLScriptElement): boolean {
    return this.document.getElementById(INSTANT_CHAT_SCRIPT_ID) === script;
  }

  private findOpener(): (() => void) | null {
    const vendor = (this.document.defaultView as InstantChatWindow | null)?.lh_inst;
    if (typeof vendor?.lh_openchatWindow === 'function') {
      return () => vendor.lh_openchatWindow();
    }
    const launcher = this.document.getElementById(INSTANT_CHAT_LAUNCHER_ID);
    return launcher ? () => launcher.click() : null;
  }

  /** O launcher próprio do Instant só fica oculto quando o botão do portal tem como abrir o chat. */
  private syncNativeLauncher(): void {
    const show = this.active && this.statusSignal() !== 'loading' && !this.findOpener();
    this.document.documentElement.classList.toggle(INSTANT_CHAT_NATIVE_LAUNCHER_CLASS, show);
  }

  private clearLoadTimeout(): void {
    clearTimeout(this.loadTimeout);
    this.loadTimeout = undefined;
  }

  /** O Instant recria a janela a cada abertura: o botão de expandir precisa ser reinserido no cabeçalho novo. */
  private watchVendorDom(): void {
    if (this.vendorObserver || !this.document.body) return;
    this.vendorObserver = new MutationObserver(() => this.syncVendorDom());
    this.vendorObserver.observe(this.document.body, { childList: true });
    this.document.addEventListener('keydown', this.onKeydown);
    this.syncVendorDom();
  }

  private syncVendorDom(): void {
    const container = this.document.getElementById(VENDOR_CONTAINER_ID);
    if (!container || container.classList.contains(VENDOR_HIDDEN_CLASS)) {
      this.setExpanded(false);
    }
    if (!container) return;
    this.vendorObserver?.observe(container, { attributes: true, attributeFilter: ['class'] });
    this.ensureExpandButton(container);
  }

  private ensureExpandButton(container: HTMLElement): void {
    const header = container.querySelector<HTMLElement>(`#${VENDOR_HEADER_ID}`);
    if (!header || header.querySelector(`#${INSTANT_CHAT_EXPAND_BUTTON_ID}`)) return;

    const button = this.document.createElement('button');
    button.type = 'button';
    button.id = INSTANT_CHAT_EXPAND_BUTTON_ID;
    // O cabeçalho do Instant é arrastável: sem isto o clique iniciaria o arraste.
    button.addEventListener('mousedown', (event) => event.stopPropagation());
    button.addEventListener('touchstart', (event) => event.stopPropagation());
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.toggleExpanded();
    });
    header.insertBefore(button, header.querySelector(`:scope > #${VENDOR_MINIMIZE_ID}`));
    this.renderExpandButton(button);
  }

  private setExpanded(expanded: boolean): void {
    this.expandedSignal.set(expanded);
    this.document.documentElement.classList.toggle(INSTANT_CHAT_EXPANDED_CLASS, expanded);
    const button = this.document.getElementById(INSTANT_CHAT_EXPAND_BUTTON_ID);
    if (button) this.renderExpandButton(button);
  }

  private renderExpandButton(button: HTMLElement): void {
    const expanded = this.expandedSignal();
    const label = expanded ? 'Restaurar tamanho do chat' : 'Expandir chat';
    button.textContent = expanded ? 'close_fullscreen' : 'open_in_full';
    button.title = label;
    button.setAttribute('aria-label', label);
    button.setAttribute('aria-pressed', String(expanded));
  }
}
